/**
 * Generate a 1080×1080 PNG share card with the Ganesha reading.
 * Adopts #180's Implementation A (native Canvas 2D) — same signature for drop-in swap if needed.
 */
const SIZE = 1080;

const LEVEL_COLORS = {
  1: '#2E7D32',
  2: '#66BB6A',
  3: '#FFB300',
  4: '#EF6C00',
  5: '#D84315',
};

const LEVEL_LABELS_EN = {
  1: 'Highly Favorable',
  2: 'Favorable',
  3: 'Neutral',
  4: 'Cautionary',
  5: 'Unfavorable',
};

const LEVEL_LABELS_TA = {
  1: 'மிகவும் சாதகம்',
  2: 'சாதகம்',
  3: 'நடுநிலை',
  4: 'எச்சரிக்கை',
  5: 'சாதகமற்றது',
};

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

async function renderCanvas(cellIndex, reading, lang) {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  // Vermilion/orange/gold gradient background
  const bg = ctx.createLinearGradient(0, 0, 0, SIZE);
  bg.addColorStop(0, '#FFFBF0');
  bg.addColorStop(0.5, '#FFD9B3');
  bg.addColorStop(1, '#FFAE6B');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  ctx.strokeStyle = '#EF6C00';
  ctx.lineWidth = 6;
  ctx.strokeRect(30, 30, SIZE - 60, SIZE - 60);

  // Ganesha motif top
  ctx.font = '72px "Noto Sans Tamil", serif';
  ctx.textAlign = 'center';
  ctx.fillText('🕉️', SIZE / 2, 110);

  // Title
  ctx.fillStyle = '#8B3A00';
  ctx.font = '600 44px "Noto Sans Tamil", "Playfair Display", serif';
  ctx.fillText("Sri Ganesha's Guidance", SIZE / 2, 180);

  // Cell header
  ctx.fillStyle = '#5D2A00';
  ctx.font = '500 36px "Noto Sans Tamil", sans-serif';
  const cellLabel = lang === 'ta' ? `கட்டம் ${cellIndex}` : `Cell ${cellIndex}`;
  const syllable = lang === 'ta' ? reading.syllable_ta : reading.syllable;
  ctx.fillText(`${cellLabel}  ·  ${syllable}`, SIZE / 2, 250);

  // Auspiciousness text
  const auspLabel = lang === 'ta'
    ? LEVEL_LABELS_TA[reading.auspiciousness]
    : LEVEL_LABELS_EN[reading.auspiciousness];
  const auspTextEn = `Auspiciousness: ${auspLabel} (level ${reading.auspiciousness} of 5)`;
  const auspTextTa = `சாதகத்தன்மை: ${auspLabel} (${reading.auspiciousness}/5)`;
  ctx.fillStyle = LEVEL_COLORS[reading.auspiciousness];
  ctx.font = '600 28px "Noto Sans Tamil", sans-serif';
  ctx.fillText(lang === 'ta' ? auspTextTa : auspTextEn, SIZE / 2, 310);

  // Verse (Devanagari)
  ctx.textAlign = 'center';
  ctx.fillStyle = '#2C1810';
  ctx.font = '500 32px "Noto Sans Devanagari", serif';
  const devLines = wrapText(ctx, reading.verse, SIZE - 160);
  let y = 390;
  devLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 46;
  });

  // Verse (Tamil transliteration)
  ctx.font = '400 26px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#6B4226';
  const taLines = wrapText(ctx, reading.verse_ta, SIZE - 160);
  y += 16;
  taLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 36;
  });

  // Interpretation (primary)
  const interp = lang === 'ta' ? reading.interpretation_ta : reading.interpretation_en;
  ctx.font = '400 28px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#2C1810';
  const interpLines = wrapText(ctx, interp, SIZE - 200);
  y += 30;
  interpLines.forEach(line => {
    ctx.fillText(line, SIZE / 2, y);
    y += 40;
  });

  // Conditional remedy
  if ((reading.auspiciousness === 4 || reading.auspiciousness === 5)
       && reading.remedy_ta && reading.remedy_en) {
    const remedy = lang === 'ta' ? reading.remedy_ta : reading.remedy_en;
    ctx.font = '600 22px "Noto Sans Tamil", sans-serif';
    ctx.fillStyle = '#8B3A00';
    y += 30;
    ctx.fillText(lang === 'ta' ? 'பரிகாரம் / Remedy' : 'Remedy', SIZE / 2, y);
    ctx.font = '400 24px "Noto Sans Tamil", sans-serif';
    ctx.fillStyle = '#2C1810';
    const rLines = wrapText(ctx, remedy, SIZE - 200);
    y += 36;
    rLines.forEach(line => {
      ctx.fillText(line, SIZE / 2, y);
      y += 34;
    });
  }

  // Attribution
  ctx.font = '400 20px "Noto Sans Tamil", sans-serif';
  ctx.fillStyle = '#8B3A00';
  ctx.fillText('via JyotishSastra', SIZE / 2, SIZE - 60);

  const dataUrl = canvas.toDataURL('image/png');
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  return { dataUrl, blob };
}

export default async function ganeshaShareImageRenderer(cellIndex, reading, lang) {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // continue without font wait
    }
  }
  return renderCanvas(cellIndex, reading, lang);
}
